<template>
  <div>
    <BaseHeader
      :title="$t('lead.title')"
      :style-variant="16"
    >
      <template
        v-if="!isPlatformBottimmo"
        v-slot:design
      >
        <HeaderDesign />
      </template>

      <template v-slot:desc>
        <p>
          {{ $t('lead.subtitle') }}
        </p>
      </template>
      <template v-slot:content>
        <v-breadcrumbs :items="breadcrumbItems" />
      </template>
    </BaseHeader>
    <v-row v-if="lead">
      <v-col
        cols="12"
        md="4"
      >
        <base-info
          :lead="lead"
          :is-master-view="isMasterView"
          :is-lead-deleted-by-crm-provider="isLeadDeletedByCrmProvider"
          @lead-updated="refetchLead"
        />
        <mail-info
          v-if="!isHoppermationActivated"
          :lead="lead"
          @unsubscribed="refetchLead"
        />

        <!-- Do not show crm tags box when feature is inactive. At the moment -> Platform SMG-->
        <Feature :feature-slug="featureNames.CRM">
          <!--    Show crm tags box when active & tags exist.
                  Show crm tags box when inactive but marked as "used by user"-->
          <Tags
            v-if="
              !isMasterView &&
              (crmConfig?.isActive ? hasCrmTags : crmConfig?.isUsedByUser || false) &&
              lead?.crmLink
            "
            @updateCrmCompanyConfig="updateCrmCompanyConfig"
            @triggerTracking="triggerTracking"
            :lead="lead"
          />
        </Feature>

        <Conversions
          v-if="isHoppermationFeatureActive"
          :lead="lead"
        />

        <Permission permission="lead:invite">
          <Feature :feature-slug="featureNames.ACQUISITION_BOOSTER">
            <Invitation
              v-if="!isMasterView"
              class="mt-4"
              :lead="lead"
              :is-lead-deleted-by-crm-provider="isLeadDeletedByCrmProvider"
              :on-refetch="refetchLead"
            />
          </Feature>
        </Permission>

        <status
          :lead-id="lead.id"
          :status="lead.currentStatus"
          :assigned-user-id="lead.assignedUser && lead.assignedUser.id"
          :is-master-view="isMasterView"
          @assigned-user-changed="refetchLead"
        />

        <Permission permission="lead:write">
          <commentary
            :is-master-view="isMasterView"
            :lead-id="lead.id"
          />
        </Permission>

        <Permission permission="lead:export">
          <template v-if="hasExport">
            <v-card
              border
              class="mt-4"
            >
              <div class="pt-12 pb-2 pl-4 bg-primary">
                <span class="text-h6 text-white">
                  {{ $t('lead.export.title') }}
                </span>
              </div>
              <div class="pa-4">
                <Feature :feature-slug="featureNames.LEAD_EXPORT">
                  <div>
                    <p class="text-subtitle-2 ma-0 pa-0">
                      {{ $t('lead.export.body-texts.0') }}
                    </p>
                    <div class="text-grey text--darken-1 text-caption">
                      {{ $t('lead.export.body-texts.1') }}:
                      {{ lead.exportedAt ? formatDate(lead.exportedAt) : $t('labels.never') }}
                    </div>
                    <div class="justify-end mt-2 d-flex">
                      <v-btn
                        v-if="!isMasterView"
                        size="small"
                        color="primary"
                        @click.prevent="downloadLead"
                      >
                        {{ $t('buttons.lead.export.export-csv') }}
                      </v-btn>
                    </div>
                  </div>
                </Feature>
                <Feature :feature-slug="featureNames.CRM">
                  <Crm
                    :class="{ 'mt-4': isFeatureActive(featureNames.LEAD_EXPORT) }"
                    :lead="lead"
                    :is-master-view="isMasterView"
                  />
                </Feature>
              </div>
            </v-card>
          </template>
        </Permission>

        <Permission permission="lead:delete">
          <Feature :feature-slug="featureNames.DELETE_CONTACT">
            <DeleteContactContainer
              v-if="!isMasterView"
              :lead="lead"
            />
          </Feature>
        </Permission>
      </v-col>
      <v-col
        cols="12"
        md="8"
      >
        <lead-property-map
          v-if="hasMap"
          :lead="lead"
        />
        <Feature :feature-slug="featureNames.LEAD_AI_SUMMARY">
          <LeadAiSummary
            class="mb-4"
            :lead="lead"
            :is-master-view="isMasterView"
          />
        </Feature>
        <TimelineFilter @filter-change="timelineFilter = $event" />
        <timeline
          :timeline-filter="timelineFilter"
          :lead="lead"
        />
      </v-col>
    </v-row>
    <v-alert
      v-if="error"
      type="error"
    >
      <span v-if="error === 'NOT_FOUND'">
        {{ $t('alerts.lead.error-no-lead') }}
      </span>
      <span v-else> {{ $t('alerts.lead.error') }} ({{ error }}). </span>
    </v-alert>
  </div>
</template>

<script>
import BaseHeader from '@/components/BaseHeader.vue'
import Permission from '@/components/Permission'
import { exportLead } from '@/lib/export'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import brandingMixin from '@/mixins/branding.js'
import crmMixin from '@/mixins/crm'
import featureMixin from '@/mixins/feature'
import TimelineFilter from '@/modules/leads/Lead/Timeline/TimelineFilter.vue'
import CREATE_OR_UPDATE_CRM_COMPANY_CONFIG from '@/modules/settings/crmSettings/queries/CreateOrUpdateCrmCompanyConfig.gql'

import MAIL_PROVIDER from '../queries/MailProvider.gql'
import BaseInfo from './BaseInfo'
import Commentary from './Commentary'
import Conversions from './Conversions.vue'
import Crm from './Crm'
import DeleteContactContainer from './DeleteContactContainer'
import HeaderDesign from './HeaderDesign.vue'
import Invitation from './Invitation'
import LeadAiSummary from './LeadAiSummary.vue'
import LeadPropertyMap from './LeadPropertyMap'
import MailInfo from './MailInfo'
import LEAD from './queries/Lead.gql'
import Status from './Status'
import Tags from './Tags'
import Timeline from './Timeline'

export default {
  inject: ['crmConfigData'],
  components: {
    TimelineFilter,
    BaseInfo,
    MailInfo,
    Timeline,
    Status,
    Commentary,
    LeadPropertyMap,
    Invitation,
    BaseHeader,
    HeaderDesign,
    LeadAiSummary,
    Conversions,
    DeleteContactContainer,
    Crm,
    Tags,
    Permission,
  },
  mixins: [featureMixin, crmMixin, brandingMixin],
  apollo: {
    mailProvider: {
      query: MAIL_PROVIDER,
      variables() {
        return {
          companyId: this.$auth.user.companyId,
        }
      },
      update: (data) => data.company.mailProvider,
    },
    lead: {
      query: LEAD,
      variables() {
        return {
          id: this.$route.params.id,
        }
      },
      pollInterval: 60000,
      error(error) {
        this.error = error.graphQLErrors[0]?.extensions?.code
      },
    },
  },
  mounted() {
    this.crmConfig = this.crmConfigData.value
  },
  data() {
    return {
      lead: null,
      error: null,
      crmConfig: null,
      timelineFilter: null,
    }
  },
  computed: {
    isLeadDeletedByCrmProvider() {
      return this.lead?.activities?.some((activity) => activity.type === 'CRM_LEAD_DELETED')
    },
    isMasterView() {
      return this.$route.path.startsWith('/master')
    },
    hasCrmTags() {
      return this.lead.tags.some((tag) => tag.startsWith('crm:'))
    },
    isHoppermationFeatureActive() {
      return this.isFeatureActive(this.featureNames.HOPPERMATION)
    },
    isHoppermationActivated() {
      return this.mailProvider === 'HOPPERMATION'
    },
    breadcrumbItems() {
      return [
        {
          title: this.$t('leads.title'),
          to: this.isMasterView
            ? `/master/lead-list/${this.$route?.params?.childCompanyId || ''}`
            : `/leads${this.$route?.hash || ''}`,
          disabled: false,
          exact: true,
        },
        { title: 'Details' },
      ]
    },
    hasMap() {
      const hasValuation = this.lead.valuations.length > 0
      const hasPlotValuation = this.lead.conversions.some(
        (conversion) => conversion.type === 'PLOT_VALUATION'
      )
      const hasPropertyRequest = this.lead.conversions.some(
        (conversion) => conversion.type === 'PROPERTY_REQUEST'
      )

      return hasValuation || hasPlotValuation || hasPropertyRequest
    },
    hasExport() {
      return (
        this.isFeatureActive(this.featureNames.LEAD_EXPORT) ||
        this.isFeatureActive(this.featureNames.CRM)
      )
    },
  },
  methods: {
    triggerTracking() {
      this.$tracking.event('Settings', 'Clicked', 'Create or Update CRM Company Config')
    },
    async updateCrmCompanyConfig() {
      try {
        await this.$apollo.mutate({
          mutation: CREATE_OR_UPDATE_CRM_COMPANY_CONFIG,
          variables: {
            input: {
              companyId: this.$auth.user.companyId,
              isUsedByUser: false,
              isActive: false,
            },
          },
        })

        showSnackbarMessage('info', this.$t('alerts.settings.crm.success'))
      } catch (error) {
        showSnackbarMessage('error', this.$t('alerts.settings.crm.error'))
      }
    },
    async downloadLead() {
      await exportLead({ id: this.lead.id })
      await this.$apollo.queries.lead.refetch()
      this.$tracking.event('Lead', 'Clicked', 'Export Lead')
    },
    async refetchLead() {
      await this.$apollo.queries.lead.refetch()
    },
  },
}
</script>
